import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star, LogOut, ClipboardList, Wrench, MapPin, MessageSquare, Images, Loader2, Settings } from "lucide-react";
import { api } from "../lib/api";
import { RequestsTab } from "../components/admin/RequestsTab";
import { ServicesTab } from "../components/admin/ServicesTab";
import { AreasTab } from "../components/admin/AreasTab";
import { ReviewsTab } from "../components/admin/ReviewsTab";
import { GalleryTab } from "../components/admin/GalleryTab";
import { SettingsTab } from "../components/admin/SettingsTab";

const TABS = [
  { id: "requests", label: "الطلبات", icon: ClipboardList },
  { id: "services", label: "الخدمات", icon: Wrench },
  { id: "areas", label: "المناطق", icon: MapPin },
  { id: "reviews", label: "التقييمات", icon: MessageSquare },
  { id: "gallery", label: "المعرض", icon: Images },
  { id: "settings", label: "الإعدادات", icon: Settings },
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("requests");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => nav("/admin/login"));
  }, [nav]);

  useEffect(() => {
    if (user) api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [user, tab]);

  const logout = async () => {
    localStorage.removeItem("najm_admin_token");
    try { await api.post("/auth/logout"); } catch {}
    nav("/admin/login");
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-yellow-400 animate-spin" />
      </div>
    );
  }

  const statCards = [
    { label: "طلبات جديدة", value: stats?.requests_new, testid: "stat-new-requests" },
    { label: "إجمالي الطلبات", value: stats?.requests_total, testid: "stat-total-requests" },
    { label: "الخدمات", value: stats?.services, testid: "stat-services" },
    { label: "تقييمات بالانتظار", value: stats?.reviews_pending, testid: "stat-pending-reviews" },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0A0A] text-zinc-100">
      <header className="border-b border-[#1c1c20] bg-[#0d0d0f] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-yellow-400 flex items-center justify-center rotate-3">
              <Star className="w-4 h-4 text-black" fill="currentColor" />
            </span>
            <span className="font-display font-bold text-lg text-white">بنشر متنقل نجم الإطارات — لوحة التحكم</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-zinc-500 hidden sm:block" dir="ltr">{user.email}</span>
            <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-red-400 transition-colors">
              <LogOut className="w-4 h-4" /> خروج
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((s) => (
            <div key={s.label} data-testid={s.testid} className="bg-[#121214] border border-[#28282D] rounded-xl p-5">
              <div className="font-display text-3xl font-extrabold text-yellow-400">{s.value ?? "—"}</div>
              <div className="text-xs text-zinc-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
                tab === t.id ? "bg-yellow-400 text-black" : "border border-[#28282D] text-zinc-400 hover:border-yellow-400/50"
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
              {t.id === "requests" && stats?.requests_new > 0 && (
                <span data-testid="tab-requests-badge" className={`text-[10px] font-bold rounded-full min-w-5 h-5 px-1 flex items-center justify-center ${tab === "requests" ? "bg-black text-yellow-400" : "bg-red-500 text-white"}`}>
                  {stats.requests_new}
                </span>
              )}
            </button>
          ))}
        </div>

        {tab === "requests" && <RequestsTab />}
        {tab === "services" && <ServicesTab />}
        {tab === "areas" && <AreasTab />}
        {tab === "reviews" && <ReviewsTab />}
        {tab === "gallery" && <GalleryTab />}
        {tab === "settings" && <SettingsTab />}
      </div>
    </div>
  );
}
